/**
 * InsightsPanel - Collapsible panel displaying auto-generated financial insights
 *
 * Features:
 * - Collapsible header with "Insights do Período" title
 * - Animated expand/collapse (respects prefers-reduced-motion)
 * - Themed insight cards (positive/negative/warning/neutral)
 * - Optional action links for drill-down navigation
 * - WCAG AA accessible (proper aria-labels, keyboard navigation)
 */

import { useState, useCallback } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import type { Insight, InsightType } from '../types/insights';

/**
 * Props for the InsightsPanel component
 */
export interface InsightsPanelProps {
  /** Array of insights to display */
  insights: Insight[];
  /** Whether the panel is initially expanded */
  defaultExpanded?: boolean;
  /** Optional title override */
  title?: string;
  /** Optional className for styling */
  className?: string;
  /** Callback when action link is clicked */
  onActionClick?: (insight: Insight) => void;
}

/**
 * Style configuration for each insight type
 */
const INSIGHT_STYLES: Record<InsightType, {
  bgClass: string;
  borderClass: string;
  textClass: string;
  iconBgClass: string;
}> = {
  positive: {
    bgClass: 'bg-success/10',
    borderClass: 'border-success/30',
    textClass: 'text-success',
    iconBgClass: 'bg-success/20',
  },
  negative: {
    bgClass: 'bg-error/10',
    borderClass: 'border-error/30',
    textClass: 'text-error',
    iconBgClass: 'bg-error/20',
  },
  warning: {
    bgClass: 'bg-warning/10',
    borderClass: 'border-warning/30',
    textClass: 'text-warning',
    iconBgClass: 'bg-warning/20',
  },
  neutral: {
    bgClass: 'bg-brand-gray/10',
    borderClass: 'border-brand-gray/30',
    textClass: 'text-brand-gray',
    iconBgClass: 'bg-brand-gray/20',
  },
};

/**
 * Get accessible label for insight type
 */
function getInsightTypeLabel(type: InsightType): string {
  switch (type) {
    case 'positive':
      return 'Insight positivo';
    case 'negative':
      return 'Insight negativo';
    case 'warning':
      return 'Insight de alerta';
    case 'neutral':
      return 'Insight informativo';
  }
}

/**
 * Individual Insight Card Component
 */
interface InsightCardProps {
  insight: Insight;
  onActionClick?: (insight: Insight) => void;
}

function InsightCard({ insight, onActionClick }: InsightCardProps) {
  const styles = INSIGHT_STYLES[insight.type];
  const typeLabel = getInsightTypeLabel(insight.type);

  const handleActionClick = useCallback(() => {
    if (onActionClick) {
      onActionClick(insight);
    } else if (insight.actionLink) {
      window.location.href = insight.actionLink;
    }
  }, [insight, onActionClick]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActionClick();
    }
  }, [handleActionClick]);

  return (
    <div
      className={`
        p-4 rounded-lg border transition-all duration-150
        motion-safe:hover:scale-[1.01]
        ${styles.bgClass}
        ${styles.borderClass}
      `}
      role="article"
      aria-label={`${typeLabel}: ${insight.title}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`
            flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-xl
            ${styles.iconBgClass}
          `}
          aria-hidden="true"
        >
          {insight.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-sm mb-1 ${styles.textClass}`}>
            {insight.title}
          </h4>
          <p className="text-sm text-brand-gray-light leading-relaxed">
            {insight.detail}
          </p>

          {/* Action Link */}
          {insight.actionLink && (
            <button
              onClick={handleActionClick}
              onKeyDown={handleKeyDown}
              className={`
                mt-2 inline-flex items-center gap-1 text-xs font-medium
                ${styles.textClass}
                hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2
                focus:ring-offset-brand-dark focus:ring-brand-primary rounded
                transition-colors duration-150
              `}
              aria-label={insight.actionLabel || 'Ver detalhes'}
            >
              {insight.actionLabel || 'Ver detalhes'}
              <ExternalLink className="w-3 h-3" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * InsightsPanel Component
 */
export function InsightsPanel({
  insights,
  defaultExpanded = true,
  title = 'Insights do Período',
  className = '',
  onActionClick,
}: InsightsPanelProps): JSX.Element | null {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded();
    }
  }, [toggleExpanded]);

  // Don't render if no insights
  if (insights.length === 0) {
    return null;
  }

  // Count insights by type for summary
  const insightCounts = insights.reduce((acc, insight) => {
    acc[insight.type] = (acc[insight.type] || 0) + 1;
    return acc;
  }, {} as Record<InsightType, number>);

  return (
    <div
      className={`
        bg-brand-dark-light/50 rounded-monumenta border border-gray-800/50
        overflow-hidden
        ${className}
      `}
    >
      {/* Header */}
      <button
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        aria-expanded={isExpanded}
        aria-controls="insights-panel-content"
        className={`
          w-full flex items-center justify-between p-4
          text-left transition-colors duration-150
          hover:bg-brand-dark-light/70 focus:outline-none
          focus:ring-2 focus:ring-inset focus:ring-brand-primary
        `}
      >
        <div className="flex items-center gap-3">
          {/* Expand/Collapse Icon */}
          <span
            className="text-brand-gray transition-transform duration-200 motion-reduce:transition-none"
            aria-hidden="true"
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </span>

          {/* Title */}
          <h3 className="font-semibold text-white text-lg">{title}</h3>

          {/* Insight Count Badges */}
          <div className="flex items-center gap-1.5">
            {insightCounts.positive && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-full bg-success/20 text-success"
                aria-label={`${insightCounts.positive} insight${insightCounts.positive > 1 ? 's' : ''} positivo${insightCounts.positive > 1 ? 's' : ''}`}
              >
                {insightCounts.positive}
              </span>
            )}
            {insightCounts.warning && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-full bg-warning/20 text-warning"
                aria-label={`${insightCounts.warning} alerta${insightCounts.warning > 1 ? 's' : ''}`}
              >
                {insightCounts.warning}
              </span>
            )}
            {insightCounts.negative && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-full bg-error/20 text-error"
                aria-label={`${insightCounts.negative} insight${insightCounts.negative > 1 ? 's' : ''} negativo${insightCounts.negative > 1 ? 's' : ''}`}
              >
                {insightCounts.negative}
              </span>
            )}
            {insightCounts.neutral && (
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-full bg-brand-gray/20 text-brand-gray"
                aria-label={`${insightCounts.neutral} insight${insightCounts.neutral > 1 ? 's' : ''} informativo${insightCounts.neutral > 1 ? 's' : ''}`}
              >
                {insightCounts.neutral}
              </span>
            )}
          </div>
        </div>

        {/* Collapse indicator text */}
        <span className="text-xs text-brand-gray">
          {isExpanded ? 'Recolher' : 'Expandir'}
        </span>
      </button>

      {/* Content */}
      <div
        id="insights-panel-content"
        className={`
          transition-all duration-300 ease-in-out
          motion-reduce:transition-none
          ${isExpanded
            ? 'max-h-[2000px] opacity-100'
            : 'max-h-0 opacity-0 overflow-hidden'
          }
        `}
      >
        <div className="p-4 pt-0 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {insights.map(insight => (
            <InsightCard
              key={insight.id}
              insight={insight}
              onActionClick={onActionClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default InsightsPanel;
